import React, { useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Crosshair, Target, MapTrifold, Wind, Trophy, Lightning, Binoculars, Scan } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { isStandalone } from '../services/installPrompt';

const TIER_FEATURES = {
  free:  ['Carte de base', '5 sites', 'Météo', 'Recherche IA: 19.99$'],
  pro:   ['Carte HD', '10 sites', 'IA Vent', 'Recherche IA: 19.99$'],
  elite: ['Tout Pro', '1 recherche IA gratuite', 'Suivantes: 19.99$', 'Support prioritaire'],
  ultra: ['Tout Elite', '2 recherches IA gratuites', 'Suivantes: 9.99$', 'Données premium'],
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Capture ?ref=XXX from incoming URL and stash in localStorage so it survives
  // the journey to the signup form (works even if user lands here from TikTok/Insta).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && ref.length >= 4 && ref.length <= 12) {
      try { localStorage.setItem('obgh_ref_code', ref.toUpperCase().replace(/[^A-Z0-9]/g, '')); } catch (_) {}
    }
  }, []);

  // If already authenticated, skip the landing — installed app (mode standalone) opens straight on the map
  if (loading) return null;
  if (user) return <Navigate to={isStandalone() ? '/map' : '/dashboard'} replace />;

  return (
    <div className="min-h-screen bg-obsidian text-white font-body">
      {/* Tactical Nav */}
      <nav className="fixed top-0 w-full z-50 glass-panel border-b border-white/10">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          {/* Logo + Name */}
          <div className="flex items-center gap-4">
            <img
              src="/obgh-logo-v2.png"
              alt="OBGH"
              className="h-14 sm:h-20 w-auto drop-shadow-[0_0_12px_rgba(255,107,53,0.4)]"
            />
            <div className="flex flex-col">
              <span className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tighter text-white leading-none">OBGH</span>
              <span className="font-heading text-sm sm:text-xl uppercase tracking-[0.3em] text-blaze font-bold leading-none mt-1">TERRITOIRE PRO</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/login')}
              className="px-3 py-2 text-xs sm:text-sm text-zinc-400 hover:text-blaze transition-all font-semibold uppercase tracking-wider border border-transparent hover:border-blaze/50"
              data-testid="nav-login-btn"
            >
              Connexion
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-3 py-2 sm:px-4 sm:py-2 bg-blaze hover:bg-blaze-bright text-obsidian font-bold uppercase tracking-wider text-xs sm:text-sm transition-all border border-blaze"
              data-testid="nav-register-btn"
            >
              <span className="hidden sm:inline">Commencer</span>
              <span className="sm:hidden">Démarrer</span>
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/32145292/pexels-photo-32145292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-transparent" />
        </div>
        <div className="absolute inset-0 tactical-grid opacity-30 z-0" />

        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blaze-muted border border-blaze/50 mb-6">
              <Crosshair size={16} weight="bold" className="text-blaze" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-blaze">
                CONÇU POUR LES CHASSEURS QUÉBÉCOIS
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-black uppercase tracking-tight leading-none mb-6">
              L'Application de Chasse
              <br />
              <span className="text-blaze">Intelligente du Québec</span>
            </h1>

            <p className="text-lg text-zinc-300 mb-8 leading-relaxed max-w-2xl">
              Carte interactive HD, terrains publics/privés, IA de positionnement selon le vent, portefeuille de permis numérique, et l'IA Chien de Sang révolutionnaire.
            </p>

            <div className="flex flex-wrap gap-4">
              <button onClick={() => navigate('/register')} className="btn-tactical" data-testid="hero-start-free">
                <Lightning size={20} weight="fill" className="inline mr-2" />
                Commencer Gratuitement
              </button>
              <button onClick={() => navigate('/login')} className="btn-tactical-secondary px-6 py-3" data-testid="hero-demo">
                Voir la Démo
              </button>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
              <Stat value="99.9%" label="Précision IA" />
              <Stat value="24/7"  label="Support" />
              <Stat value="4"     label="Plans Pro" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 bg-obsidian-panel relative">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/6f86f734-6962-46b1-8099-110346aa7643/images/0bef1cf76e4713745090b7627ec1e9f0bfb13bdf5309e4ec50c26b7508b88c99.png)',
            backgroundSize: 'cover',
            mixBlendMode: 'overlay',
          }}
        />
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-blaze mb-4 block">FONCTIONNALITÉS</span>
            <h2 className="text-4xl sm:text-5xl font-heading font-black uppercase tracking-tight">
              Outils Tactiques Avancés
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <TacticalCard icon={<MapTrifold size={32} weight="duotone" />}  title="Carte Interactive HD"    description="Terrains publics/privés, topographie précise, lots en acres" />
            <TacticalCard icon={<Wind size={32} weight="duotone" />}        title="IA Positionnement"       description="Direction du vent en temps réel, prédictions comportementales" />
            <TacticalCard icon={<Scan size={32} weight="duotone" />}        title="IA Chien de Sang"        description="Satellite thermique + Vision IA pour récupération" highlight />
            <TacticalCard icon={<Trophy size={32} weight="duotone" />}      title="Portefeuille Digital"    description="Permis numériques, enregistrement 1-clic" />
          </div>
        </div>
      </section>

      {/* Blood Tracker AI Showcase */}
      <section className="py-24 px-6 bg-obsidian relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-blaze mb-4 block">FONCTIONNALITÉ PHARE</span>
              <h2 className="text-4xl sm:text-5xl font-heading font-black uppercase tracking-tight mb-6">
                IA Chien de Sang
                <br />
                <span className="text-blaze">Révolutionnaire</span>
              </h2>
              <p className="text-zinc-300 mb-6 leading-relaxed">
                Imagerie satellite thermique combinée à l'intelligence artificielle et à la détection de pistage thermique comportemental pour localiser le gibier blessé avec une précision militaire.
              </p>

              <div className="mb-6 p-3 border border-zinc-700/50 rounded-sm bg-zinc-900/30">
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest text-center font-bold">
                  Technologies exclusives et propriétaires OBGH — Protégées par les lois canadiennes et internationales sur la propriété intellectuelle
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <Target size={20} className="text-blaze mt-1" weight="duotone" />
                  <span className="text-zinc-300">Prédictions IA en temps réel basées sur le comportement animal</span>
                </li>
                <li className="flex items-start gap-3">
                  <Scan size={20} className="text-blaze mt-1" weight="duotone" />
                  <span className="text-zinc-300">Images satellite thermiques pour détection de chaleur</span>
                </li>
                <li className="flex items-start gap-3">
                  <Binoculars size={20} className="text-blaze mt-1" weight="duotone" />
                  <span className="text-zinc-300">Vision IA pour traces de sang invisibles</span>
                </li>
              </ul>

              <button onClick={() => navigate('/register')} className="btn-tactical" data-testid="blood-cta">
                Essayer l'IA Chien de Sang
              </button>
            </div>

            <div className="relative">
              <div className="scanlines rounded-sm overflow-hidden tactical-border">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/6f86f734-6962-46b1-8099-110346aa7643/images/8414af7162ba9475bc468841b0bd40f272115a732357a94d6fe0f48eec31bc05.png"
                  alt="IA Chien de Sang"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute top-0 left-0 w-full h-1 bg-blaze/50 animate-scan" />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-6 bg-obsidian-panel">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-blaze mb-4 block">TARIFICATION</span>
            <h2 className="text-4xl sm:text-5xl font-heading font-black uppercase tracking-tight">
              Choisissez Votre Niveau
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            <PricingCard tier="GRATUIT" price="0$"      per="/an" features={TIER_FEATURES.free} />
            <PricingCard tier="PRO"     price="29.99$"  oldPrice="59.99$"  per="/an" features={TIER_FEATURES.pro} />
            <PricingCard tier="ELITE"   price="39.99$"  oldPrice="99.99$" per="/an" features={TIER_FEATURES.elite} highlight />
            <PricingCard tier="ULTRA"   price="56.99$"  oldPrice="149.99$" per="/an" features={TIER_FEATURES.ultra} premium />
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 px-6 bg-obsidian relative overflow-hidden">
        <div className="absolute inset-0 tactical-grid opacity-20" />
        <div className="container mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-6xl font-heading font-black uppercase tracking-tight mb-6">
            Prêt à Dominer
            <br />
            <span className="text-blaze">Votre Territoire?</span>
          </h2>
          <p className="text-xl text-zinc-300 mb-8 max-w-2xl mx-auto">
            Rejoignez les chasseurs québécois qui utilisent OBGH pour optimiser chaque sortie.
          </p>
          <button onClick={() => navigate('/register')} className="btn-tactical text-lg px-10 py-4" data-testid="cta-start-now">
            <Lightning size={24} weight="fill" className="inline mr-2" />
            Commencer Maintenant
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-obsidian-card border-t border-white/10">
        <div className="container mx-auto text-center space-y-3">
          <div className="flex items-center justify-center gap-4">
            <a href="/terms" className="text-xs text-zinc-500 hover:text-blaze transition-colors font-mono uppercase tracking-wider" data-testid="terms-link-footer">
              Conditions d'utilisation
            </a>
            <span className="text-zinc-700">|</span>
            <a href="/terms#privacy" className="text-xs text-zinc-500 hover:text-blaze transition-colors font-mono uppercase tracking-wider" data-testid="privacy-link-footer">
              Confidentialité
            </a>
          </div>
          <p className="text-zinc-500 text-sm font-mono">© 2026 OBGH TERRITOIRE PRO. Tous droits réservés.</p>
          <p className="text-[9px] text-zinc-600 font-mono uppercase tracking-widest max-w-lg mx-auto">
            Technologies exclusives et propriétaires — Algorithmes IA, vision thermique et détection comportementale protégés par les lois canadiennes et internationales sur la propriété intellectuelle
          </p>
        </div>
      </footer>
    </div>
  );
};

const Stat = ({ value, label }) => (
  <div className="tactical-border p-4 bg-obsidian-card">
    <div className="font-mono text-2xl font-bold text-blaze">{value}</div>
    <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
  </div>
);

const TacticalCard = ({ icon, title, description, highlight }) => (
  <div className={`p-6 bg-obsidian-card tactical-border transition-all duration-150 hover:-translate-y-1 ${highlight ? 'animate-glow' : ''}`}>
    <div className={`mb-4 ${highlight ? 'text-blaze' : 'text-zinc-400'}`}>{icon}</div>
    <h3 className="text-lg font-heading font-bold uppercase tracking-tight mb-2">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{description}</p>
  </div>
);

const PricingCard = ({ tier, price, oldPrice, per, features, highlight, premium }) => (
  <div className={`p-6 bg-obsidian-card tactical-border transition-all duration-150 hover:-translate-y-1 ${highlight ? 'border-blaze/60 animate-glow' : ''} ${premium ? 'border-yellow-500/60 animate-glow' : ''}`}>
    <div className="mb-4">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">{tier}</span>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="text-3xl font-heading font-black text-blaze">{price}</div>
        {oldPrice && <span className="text-sm text-zinc-500 line-through">{oldPrice}</span>}
      </div>
      <span className="text-xs text-zinc-500">{per}</span>
      {oldPrice && (
        <div className="mt-1.5">
          <span className="inline-block px-2 py-0.5 bg-blaze/15 border border-blaze/50 rounded-sm text-[10px] font-black uppercase tracking-wider text-blaze">Offre de lancement</span>
        </div>
      )}
    </div>
    <ul className="space-y-2">
      {(Array.isArray(features) ? features : []).map((feature, i) => (
        <li key={`${tier}-${i}`} className="text-sm text-zinc-400 flex items-center gap-2">
          <Crosshair size={12} className="text-blaze" weight="bold" />
          {feature}
        </li>
      ))}
    </ul>
  </div>
);

export default LandingPage;
